import { z } from 'zod';
import { LOCALES } from '@/domain/ids';
import { db } from './database';

export const deviceSettingsSchema = z.object({
  locale: z.enum(LOCALES),
  /** Shown once: local storage limitations were explained to the parent. */
  storageNoticeSeen: z.boolean(),
  /** Last version whose "what's new" was shown on this device. */
  seenVersion: z.string().max(20).optional(),
  /** Vote round (config/ideas.ts) this device already voted in. */
  votedRound: z.string().max(20).optional(),
});
export type DeviceSettings = z.infer<typeof deviceSettingsSchema>;

export const DEFAULT_DEVICE_SETTINGS: DeviceSettings = { locale: 'ar', storageNoticeSeen: false };

export async function getMeta<T>(key: string, schema: z.ZodType<T>): Promise<T | undefined> {
  const entry = await db.meta.get(key);
  if (!entry) return undefined;
  const parsed = schema.safeParse(entry.value);
  return parsed.success ? parsed.data : undefined;
}

export async function setMeta(key: string, value: unknown): Promise<void> {
  await db.meta.put({ key, value });
}

export async function getDeviceSettings(): Promise<DeviceSettings> {
  return (await getMeta('settings', deviceSettingsSchema)) ?? DEFAULT_DEVICE_SETTINGS;
}

export async function updateDeviceSettings(
  patch: Partial<DeviceSettings>,
): Promise<DeviceSettings> {
  const next = { ...(await getDeviceSettings()), ...patch };
  await setMeta('settings', next);
  return next;
}

export async function getActiveLearnerId(): Promise<string | undefined> {
  return getMeta('activeLearnerId', z.string());
}

export async function setActiveLearnerId(id: string | undefined): Promise<void> {
  if (id) await setMeta('activeLearnerId', id);
  else await db.meta.delete('activeLearnerId');
}
